/**
 * Permission Constants
 * Granular permission definitions for the Uhamiaji SACCOS Management System
 */

export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: 'dashboard:view',
  
  // Members
  MEMBERS_VIEW: 'members:view',
  MEMBERS_CREATE: 'members:create',
  MEMBERS_EDIT: 'members:edit',
  MEMBERS_DELETE: 'members:delete',
  MEMBERS_EXPORT: 'members:export',
  MEMBERS_IMPORT: 'members:import',
  
  // Loans
  LOANS_VIEW: 'loans:view',
  LOANS_CREATE: 'loans:create',
  LOANS_EDIT: 'loans:edit',
  LOANS_DELETE: 'loans:delete',
  LOANS_APPROVE: 'loans:approve',
  LOANS_DISBURSE: 'loans:disburse',
  LOANS_EXPORT: 'loans:export',
  
  // Savings
  SAVINGS_VIEW: 'savings:view',
  SAVINGS_CREATE: 'savings:create',
  SAVINGS_EDIT: 'savings:edit',
  SAVINGS_DELETE: 'savings:delete',
  SAVINGS_DEPOSIT: 'savings:deposit',
  SAVINGS_WITHDRAW: 'savings:withdraw',
  SAVINGS_EXPORT: 'savings:export',
  
  // Shares
  SHARES_VIEW: 'shares:view',
  SHARES_CREATE: 'shares:create',
  SHARES_EDIT: 'shares:edit',
  SHARES_DELETE: 'shares:delete',
  SHARES_PURCHASE: 'shares:purchase',
  SHARES_EXPORT: 'shares:export',
  
  // Repayments
  REPAYMENTS_VIEW: 'repayments:view',
  REPAYMENTS_CREATE: 'repayments:create',
  REPAYMENTS_EDIT: 'repayments:edit',
  REPAYMENTS_DELETE: 'repayments:delete',
  REPAYMENTS_EXPORT: 'repayments:export',
  
  // Guarantors
  GUARANTORS_VIEW: 'guarantors:view',
  GUARANTORS_CREATE: 'guarantors:create',
  GUARANTORS_EDIT: 'guarantors:edit',
  GUARANTORS_DELETE: 'guarantors:delete',
  
  // Accounting
  ACCOUNTING_VIEW: 'accounting:view',
  ACCOUNTING_CREATE_TRANSACTION: 'accounting:create:transaction',
  ACCOUNTING_EDIT_TRANSACTION: 'accounting:edit:transaction',
  ACCOUNTING_DELETE_TRANSACTION: 'accounting:delete:transaction',
  ACCOUNTING_JOURNAL_ENTRIES: 'accounting:journal:entries',
  ACCOUNTING_CHART_OF_ACCOUNTS: 'accounting:chart:accounts',
  ACCOUNTING_FINANCIAL_REPORTS: 'accounting:financial:reports',
  
  // Reports
  REPORTS_VIEW: 'reports:view',
  REPORTS_CREATE: 'reports:create',
  REPORTS_EXPORT: 'reports:export',
  REPORTS_SCHEDULE: 'reports:schedule',
  
  // Users
  USERS_VIEW: 'users:view',
  USERS_CREATE: 'users:create',
  USERS_EDIT: 'users:edit',
  USERS_DELETE: 'users:delete',
  USERS_MANAGE_ROLES: 'users:manage:roles',
  USERS_MANAGE_PERMISSIONS: 'users:manage:permissions',
  
  // Settings
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_EDIT: 'settings:edit',
  SETTINGS_MANAGE_SYSTEM: 'settings:manage:system',
  SETTINGS_MANAGE_BACKUP: 'settings:manage:backup',
  SETTINGS_VIEW_AUDIT_LOG: 'settings:view:audit:log',
  
  // Notifications
  NOTIFICATIONS_VIEW: 'notifications:view',
  NOTIFICATIONS_CREATE: 'notifications:create',
  NOTIFICATIONS_MANAGE: 'notifications:manage',
  
  // All permissions wildcard
  ALL: '*',
};

/**
 * Permission groups by module
 */
export const PERMISSION_GROUPS = {
  DASHBOARD: [
    PERMISSIONS.DASHBOARD_VIEW,
  ],
  MEMBERS: [
    PERMISSIONS.MEMBERS_VIEW,
    PERMISSIONS.MEMBERS_CREATE,
    PERMISSIONS.MEMBERS_EDIT,
    PERMISSIONS.MEMBERS_DELETE,
    PERMISSIONS.MEMBERS_EXPORT,
    PERMISSIONS.MEMBERS_IMPORT,
  ],
  LOANS: [
    PERMISSIONS.LOANS_VIEW,
    PERMISSIONS.LOANS_CREATE,
    PERMISSIONS.LOANS_EDIT,
    PERMISSIONS.LOANS_DELETE,
    PERMISSIONS.LOANS_APPROVE,
    PERMISSIONS.LOANS_DISBURSE,
    PERMISSIONS.LOANS_EXPORT,
  ],
  SAVINGS: [
    PERMISSIONS.SAVINGS_VIEW,
    PERMISSIONS.SAVINGS_CREATE,
    PERMISSIONS.SAVINGS_EDIT,
    PERMISSIONS.SAVINGS_DELETE,
    PERMISSIONS.SAVINGS_DEPOSIT,
    PERMISSIONS.SAVINGS_WITHDRAW,
    PERMISSIONS.SAVINGS_EXPORT,
  ],
  SHARES: [
    PERMISSIONS.SHARES_VIEW,
    PERMISSIONS.SHARES_CREATE,
    PERMISSIONS.SHARES_EDIT,
    PERMISSIONS.SHARES_DELETE,
    PERMISSIONS.SHARES_PURCHASE,
    PERMISSIONS.SHARES_EXPORT,
  ],
  REPAYMENTS: [
    PERMISSIONS.REPAYMENTS_VIEW,
    PERMISSIONS.REPAYMENTS_CREATE,
    PERMISSIONS.REPAYMENTS_EDIT,
    PERMISSIONS.REPAYMENTS_DELETE,
    PERMISSIONS.REPAYMENTS_EXPORT,
  ],
  GUARANTORS: [
    PERMISSIONS.GUARANTORS_VIEW,
    PERMISSIONS.GUARANTORS_CREATE,
    PERMISSIONS.GUARANTORS_EDIT,
    PERMISSIONS.GUARANTORS_DELETE,
  ],
  ACCOUNTING: [
    PERMISSIONS.ACCOUNTING_VIEW,
    PERMISSIONS.ACCOUNTING_CREATE_TRANSACTION,
    PERMISSIONS.ACCOUNTING_EDIT_TRANSACTION,
    PERMISSIONS.ACCOUNTING_DELETE_TRANSACTION,
    PERMISSIONS.ACCOUNTING_JOURNAL_ENTRIES,
    PERMISSIONS.ACCOUNTING_CHART_OF_ACCOUNTS,
    PERMISSIONS.ACCOUNTING_FINANCIAL_REPORTS,
  ],
  REPORTS: [
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_CREATE,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.REPORTS_SCHEDULE,
  ],
  USERS: [
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_EDIT,
    PERMISSIONS.USERS_DELETE,
    PERMISSIONS.USERS_MANAGE_ROLES,
    PERMISSIONS.USERS_MANAGE_PERMISSIONS,
  ],
  SETTINGS: [
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.SETTINGS_EDIT,
    PERMISSIONS.SETTINGS_MANAGE_SYSTEM,
    PERMISSIONS.SETTINGS_MANAGE_BACKUP,
    PERMISSIONS.SETTINGS_VIEW_AUDIT_LOG,
  ],
  NOTIFICATIONS: [
    PERMISSIONS.NOTIFICATIONS_VIEW,
    PERMISSIONS.NOTIFICATIONS_CREATE,
    PERMISSIONS.NOTIFICATIONS_MANAGE,
  ],
};

/**
 * Default permissions by role
 */
export const DEFAULT_ROLE_PERMISSIONS = {
  admin: Object.values(PERMISSIONS),
  manager: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.MEMBERS_VIEW,
    PERMISSIONS.MEMBERS_CREATE,
    PERMISSIONS.MEMBERS_EDIT,
    PERMISSIONS.MEMBERS_EXPORT,
    PERMISSIONS.LOANS_VIEW,
    PERMISSIONS.LOANS_CREATE,
    PERMISSIONS.LOANS_EDIT,
    PERMISSIONS.LOANS_APPROVE,
    PERMISSIONS.LOANS_DISBURSE,
    PERMISSIONS.LOANS_EXPORT,
    PERMISSIONS.SAVINGS_VIEW,
    PERMISSIONS.SAVINGS_CREATE,
    PERMISSIONS.SAVINGS_EDIT,
    PERMISSIONS.SAVINGS_EXPORT,
    PERMISSIONS.SHARES_VIEW,
    PERMISSIONS.SHARES_CREATE,
    PERMISSIONS.SHARES_EDIT,
    PERMISSIONS.SHARES_EXPORT,
    PERMISSIONS.REPAYMENTS_VIEW,
    PERMISSIONS.REPAYMENTS_CREATE,
    PERMISSIONS.REPAYMENTS_EDIT,
    PERMISSIONS.REPAYMENTS_EXPORT,
    PERMISSIONS.GUARANTORS_VIEW,
    PERMISSIONS.GUARANTORS_CREATE,
    PERMISSIONS.GUARANTORS_EDIT,
    PERMISSIONS.ACCOUNTING_VIEW,
    PERMISSIONS.ACCOUNTING_CREATE_TRANSACTION,
    PERMISSIONS.ACCOUNTING_JOURNAL_ENTRIES,
    PERMISSIONS.ACCOUNTING_FINANCIAL_REPORTS,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_CREATE,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.NOTIFICATIONS_VIEW,
    PERMISSIONS.NOTIFICATIONS_CREATE,
    PERMISSIONS.SETTINGS_VIEW,
  ],
  accountant: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.MEMBERS_VIEW,
    PERMISSIONS.MEMBERS_EXPORT,
    PERMISSIONS.LOANS_VIEW,
    PERMISSIONS.LOANS_EXPORT,
    PERMISSIONS.SAVINGS_VIEW,
    PERMISSIONS.SAVINGS_CREATE,
    PERMISSIONS.SAVINGS_EDIT,
    PERMISSIONS.SAVINGS_DEPOSIT,
    PERMISSIONS.SAVINGS_WITHDRAW,
    PERMISSIONS.SAVINGS_EXPORT,
    PERMISSIONS.SHARES_VIEW,
    PERMISSIONS.SHARES_CREATE,
    PERMISSIONS.SHARES_PURCHASE,
    PERMISSIONS.SHARES_EXPORT,
    PERMISSIONS.REPAYMENTS_VIEW,
    PERMISSIONS.REPAYMENTS_CREATE,
    PERMISSIONS.REPAYMENTS_EDIT,
    PERMISSIONS.REPAYMENTS_EXPORT,
    PERMISSIONS.ACCOUNTING_VIEW,
    PERMISSIONS.ACCOUNTING_CREATE_TRANSACTION,
    PERMISSIONS.ACCOUNTING_EDIT_TRANSACTION,
    PERMISSIONS.ACCOUNTING_JOURNAL_ENTRIES,
    PERMISSIONS.ACCOUNTING_CHART_OF_ACCOUNTS,
    PERMISSIONS.ACCOUNTING_FINANCIAL_REPORTS,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_EXPORT,
  ],
  loan_officer: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.MEMBERS_VIEW,
    PERMISSIONS.MEMBERS_CREATE,
    PERMISSIONS.MEMBERS_EDIT,
    PERMISSIONS.MEMBERS_EXPORT,
    PERMISSIONS.LOANS_VIEW,
    PERMISSIONS.LOANS_CREATE,
    PERMISSIONS.LOANS_EDIT,
    PERMISSIONS.LOANS_EXPORT,
    PERMISSIONS.REPAYMENTS_VIEW,
    PERMISSIONS.REPAYMENTS_CREATE,
    PERMISSIONS.REPAYMENTS_EXPORT,
    PERMISSIONS.GUARANTORS_VIEW,
    PERMISSIONS.GUARANTORS_CREATE,
    PERMISSIONS.GUARANTORS_EDIT,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_EXPORT,
  ],
  teller: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.MEMBERS_VIEW,
    PERMISSIONS.SAVINGS_VIEW,
    PERMISSIONS.SAVINGS_DEPOSIT,
    PERMISSIONS.SAVINGS_WITHDRAW,
    PERMISSIONS.REPAYMENTS_VIEW,
    PERMISSIONS.REPAYMENTS_CREATE,
  ],
  member: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.MEMBERS_VIEW,
    PERMISSIONS.LOANS_VIEW,
    PERMISSIONS.SAVINGS_VIEW,
    PERMISSIONS.SHARES_VIEW,
    PERMISSIONS.REPAYMENTS_VIEW,
    PERMISSIONS.NOTIFICATIONS_VIEW,
  ],
  auditor: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.MEMBERS_VIEW,
    PERMISSIONS.LOANS_VIEW,
    PERMISSIONS.SAVINGS_VIEW,
    PERMISSIONS.SHARES_VIEW,
    PERMISSIONS.REPAYMENTS_VIEW,
    PERMISSIONS.GUARANTORS_VIEW,
    PERMISSIONS.ACCOUNTING_VIEW,
    PERMISSIONS.ACCOUNTING_JOURNAL_ENTRIES,
    PERMISSIONS.ACCOUNTING_CHART_OF_ACCOUNTS,
    PERMISSIONS.ACCOUNTING_FINANCIAL_REPORTS,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.SETTINGS_VIEW_AUDIT_LOG,
  ],
};

export default PERMISSIONS;
